import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../store';
import { selectorArg1 } from '../utils/selectors';
import { MaybeNil } from '../../utils/interfaces';
import { isNil } from 'lodash';
import { assertNotNil } from '../../utils/types';

const extractAnalysesFromStore = ({ analyses }: RootState) => analyses;
const combineStateWithAnalysisIds = (_: any, analysisIds: string[]) =>
  analysisIds;
const combineStateWithPropKey = (_: any, __: any, key: any) => key;
const extractPropFromModalsState = (analyses: any, analysisId: any, key: any) =>
  analyses?.[analysisId]?.modalsState?.[key];

/* Select the current analysis object */
export const analysisSelector = () =>
  createSelector(
    extractAnalysesFromStore,
    selectorArg1<MaybeNil<string>>(),
    (analyses, analysisId) => {
      if (isNil(analysisId)) {
        return null;
      }

      return analyses?.[analysisId] ?? null;
    },
  );

export const extractLoadedAnalyses = () =>
  createSelector(
    extractAnalysesFromStore,
    combineStateWithAnalysisIds,
    (analyses, analysisIds) =>
      analysisIds?.map((analysisId) => analyses?.[analysisId]),
  );

export const selectBuilderInfo = () =>
  createSelector(
    extractAnalysesFromStore,
    selectorArg1<MaybeNil<string>>(),
    (analyses, analysisId) => {
      if (isNil(analysisId)) {
        return null;
      }

      return analyses?.[analysisId]?.builderInfo;
    },
  );

export const extractModalStateFromAnalysis = () =>
  createSelector(
    extractAnalysesFromStore,
    selectorArg1<string>(),
    combineStateWithPropKey,
    extractPropFromModalsState,
  );

export const isAnalysisLoadedSelector = () =>
  createSelector(
    extractAnalysesFromStore,
    selectorArg1<string>(),
    (analyses, analysisId) => analyses.hasOwnProperty(analysisId),
  );

export const chartSettingsSnapshotSelector = () =>
  createSelector(
    extractAnalysesFromStore,
    selectorArg1<string>(),
    (analyses, analysisId) => {
      const foundAnalysis = analyses[analysisId];
      assertNotNil(foundAnalysis);
      const chartData = foundAnalysis.chartData;
      const aliasReplacements =
        chartData && 'aliasReplacementsSnapshot' in chartData
          ? chartData.aliasReplacementsSnapshot
          : null;

      const chartSettings = foundAnalysis.settings.chartSettingsSnapshot;

      return { chartSettings, aliasReplacements };
    },
  );

export const currentChartSettingsSelector = () =>
  createSelector(
    extractAnalysesFromStore,
    selectorArg1<string>(),
    (analyses, analysisId) => {
      const foundAnalysis = analyses[analysisId];
      assertNotNil(foundAnalysis);

      const chartData = foundAnalysis.chartData;
      const aliasReplacements =
        chartData && 'aliasReplacements' in chartData
          ? chartData.aliasReplacements
          : null;

      const chartSettings = foundAnalysis.settings.chartSettings ?? null;

      return { chartSettings, aliasReplacements };
    },
  );
